import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Bell, Search, AlertTriangle, XCircle, CheckCircle, Clock,
  MapPin, User, Shield, Phone, Activity, Bug, ChevronDown,
  Filter, RefreshCw, X, Eye, ExternalLink, BellOff, Layers,
  AlertCircle, Info, Wrench, MessageSquare, ChevronRight as ChevronRightIcon, Upload, Send, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import notificationsTutorial from '../components/tutorials/configs/notificationsTutorial';
import { useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import { toast } from 'react-toastify';
import '../styles/v2/pages/NotificationCenter.css';

// ═══════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════

const CATEGORIES = [
  { key: 'ALL', label: 'All' },
  { key: 'MONITORING', label: 'Monitoring' },
  { key: 'AUDIT', label: 'Audit' },
  { key: 'INTEGRATION', label: 'Integrations' },
];

const SEVERITY_CONFIG = {
  CRITICAL: { icon: XCircle, color: '#ef4444', iconClass: 'critical', label: 'Critical' },
  WARNING: { icon: AlertTriangle, color: '#f59e0b', iconClass: 'warning', label: 'Warning' },
  INFO: { icon: Info, color: '#60a5fa', iconClass: 'info', label: 'Info' },
};

const SOURCE_ICON_CLASS = {
  SYSTEM_ALERT: 'critical',
  HEALTH_ERROR: 'warning',
  AUDIT: 'audit',
  INTEGRATION: 'intercom',
};

const SOURCE_ICON = {
  SYSTEM_ALERT: AlertCircle,
  HEALTH_ERROR: Activity,
  AUDIT: Shield,
  INTEGRATION: Phone,
};

const STATUS_LABELS = {
  ACTIVE: null,
  ACKNOWLEDGED: 'Acknowledged',
  IN_PROGRESS: 'In Progress',
  SNOOZED: 'Snoozed',
  RESOLVED: 'Resolved',
  AUTO_RESOLVED: 'Auto-Resolved',
  INFO: null,
};

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getDateGroupLabel(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDate.getTime() === today.getTime()) {
    return `Today, ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
  }
  if (itemDate.getTime() === yesterday.getTime()) {
    return `Yesterday, ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
  }
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// ═══════════════════════════════════════════════════════════════
// Action Modal
// ═══════════════════════════════════════════════════════════════

const ActionModal = ({ notification, actionType, onClose, onSubmit }) => {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const titles = {
    resolve: 'Resolve Notification',
    bug: 'Report as CCM Bug',
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(notification.id, actionType, notes.trim() || undefined);
      onClose();
    } catch {
      // Error handled by parent
    }
    setSubmitting(false);
  };

  return (
    <div className="nc-modal-overlay" onClick={onClose}>
      <div className="nc-modal" onClick={e => e.stopPropagation()}>
        <h3>{titles[actionType] || 'Action'}</h3>
        <div style={{ marginBottom: 12, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
          {notification.title}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={actionType === 'bug'
            ? 'Describe the bug, steps to reproduce, expected behavior...'
            : 'Resolution notes (optional)...'
          }
          autoFocus
        />
        <div className="nc-modal-actions">
          <button className="nc-modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className={`nc-modal-submit ${actionType}`}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : actionType === 'bug' ? 'Report Bug' : 'Resolve'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Notification Item
// ═══════════════════════════════════════════════════════════════

const NotificationItem = ({ item, isSelected, onSelect, onResolve, onAcknowledge, onOpenModal }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const sevConfig = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.INFO;
  const IconComp = SOURCE_ICON[item.sourceType] || AlertCircle;
  const iconClass = SOURCE_ICON_CLASS[item.sourceType] || 'info';
  const catLabel = item.sourceType === 'SYSTEM_ALERT' ? 'Alert rule'
    : item.sourceType === 'HEALTH_ERROR' ? 'Device error'
    : item.sourceType === 'AUDIT' ? 'Audit'
    : item.sourceType === 'INTEGRATION' ? 'Integrations' : '';
  const catClass = item.sourceType === 'SYSTEM_ALERT' ? 'alert-rule' : item.sourceType === 'HEALTH_ERROR' ? 'device-error' : catLabel.toLowerCase();

  const isResolvable = ['ACTIVE', 'ACKNOWLEDGED', 'IN_PROGRESS', 'SNOOZED'].includes(item.status) && (item.sourceType === 'SYSTEM_ALERT' || item.sourceType === 'HEALTH_ERROR');
  const isAcknowledgeable = item.status === 'ACTIVE' &&
    (item.sourceType === 'SYSTEM_ALERT' || item.sourceType === 'HEALTH_ERROR');
  const isBugReportable = item.sourceType === 'HEALTH_ERROR' && !item.isCcmBug && isResolvable;
  const statusLabel = STATUS_LABELS[item.status];

  // Determine if "unread" - active critical/warning alerts are treated as unread
  const isUnread = item.status === 'ACTIVE' &&
    (item.severity === 'CRITICAL' || item.severity === 'WARNING');

  return (
    <div className={`nc-item ${isUnread ? 'unread' : ''} ${expanded ? 'expanded' : ''}`} onClick={() => setExpanded(!expanded)}>
      <input
        type="checkbox"
        className="nc-checkbox"
        checked={isSelected}
        onChange={() => onSelect(item.id)}
      />
      <div className="nc-item-icon" style={{ '--sev-color': sevConfig.color }}>
        <div className={`nc-item-icon ${iconClass}`}>
          <IconComp size={16} />
        </div>
      </div>
      <div className="nc-item-body">
        <div className="nc-item-top">
          <span className="nc-item-title">{item.title}</span>
          <span className={`nc-sev-badge ${item.severity?.toLowerCase()}`}>
            {sevConfig.label}
          </span>
          <span className={`nc-cat-badge ${catClass}`}>{catLabel}</span>
          {statusLabel && (
            <span className={`nc-status-badge ${item.status?.toLowerCase()}`}>
              {statusLabel}
            </span>
          )}
          {item.isCcmBug && (
            <span className="nc-bug-badge">
              <Bug size={10} /> {item.bugCode || 'Bug'}
            </span>
          )}
        </div>
        <div className="nc-item-msg">{item.message}</div>
        <div className="nc-item-meta">
          <span><Clock size={11} /> {formatTime(item.createdAt)}</span>
          {item.deviceName && (
            <span><Activity size={11} /> {item.deviceName}</span>
          )}
          {item.deviceIp && (
            <span><MapPin size={11} /> {item.deviceIp}</span>
          )}
          {item.userEmail && (
            <span><User size={11} /> {item.userEmail}</span>
          )}
          {item.occurrences > 1 && (
            <span><Layers size={11} /> {item.occurrences}x</span>
          )}
          {item.ruleName && (
            <span><Shield size={11} /> {item.ruleName}</span>
          )}
        </div>
      </div>
      <div className="nc-item-actions">
        {isResolvable && (
          <button className="nc-btn resolve" onClick={e => { e.stopPropagation(); onOpenModal(item, 'resolve'); }}>
            <CheckCircle size={12} /> Resolve
          </button>
        )}
        {isAcknowledgeable && (
          <button className="nc-btn" onClick={e => { e.stopPropagation(); onAcknowledge(item.id); }}>
            <Eye size={12} /> Ack
          </button>
        )}
        {isBugReportable && (
          <button className="nc-btn bug" onClick={e => { e.stopPropagation(); onOpenModal(item, 'bug'); }}>
            <Bug size={12} /> Bug
          </button>
        )}
        
      </div>
      {isUnread && <div className="nc-unread-dot" />}

      {/* Expandable Detail Section */}
      {expanded && (
        <div className="nc-detail" onClick={e => e.stopPropagation()}>
          {item.detail && (
            <div className="nc-detail-section">
              <h5>What this means</h5>
              <p>{item.detail}</p>
            </div>
          )}

          {item.notes && (
            <div className="nc-detail-section">
              <h5><MessageSquare size={13} /> Notes</h5>
              <p>{item.notes}</p>
            </div>
          )}

          {item.snoozedUntil && item.status === 'SNOOZED' && (
            <div className="nc-detail-section">
              <h5><BellOff size={13} /> Snoozed until</h5>
              <p>{new Date(item.snoozedUntil).toLocaleString()}</p>
            </div>
          )}

          {!item.isCcmBug && item.fixSteps && Array.isArray(item.fixSteps) && item.fixSteps.length > 0 && (
            <div className="nc-detail-section">
              <h5><Wrench size={13} /> How to fix</h5>
              <ol className="nc-fix-steps">
                {item.fixSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          {item.isCcmBug && (
            <div className="nc-ccm-notice">
              <Shield size={14} />
              <div>
                <strong>This is a Serverli platform issue.</strong>
                <p>Our team has been notified and is working on a fix.</p>
              </div>
            </div>
          )}

          {item.link && (item.sourceType === 'SYSTEM_ALERT' || item.sourceType === 'HEALTH_ERROR') && (
            <button
              className="nc-link-btn"
              onClick={(e) => { e.stopPropagation(); navigate(item.link); }}
            >
              <ExternalLink size={13} />
              {item.sourceType === 'HEALTH_ERROR' ? 'View device details' : 'View details'}
            </button>
          )}

          {item.raw && (
            <details className="nc-raw-details">
              <summary>Technical details</summary>
              <code>{item.raw}</code>
              {item.bugCode && <span className="nc-bugcode">Bug ID: {item.bugCode}</span>}
            </details>
          )}

          <div className="nc-detail-meta">
            {item.category && <span>Category: {item.category.replace(/_/g, ' ')}</span>}
            {item.occurrences > 1 && <span>Occurrences: {item.occurrences}</span>}
            <span>Time: {new Date(item.createdAt).toLocaleString()}</span>
            {item.resolvedAt && <span>Resolved: {new Date(item.resolvedAt).toLocaleString()}</span>}
            {item.userEmail && item.sourceType === 'AUDIT' && <span>By: {item.userEmail}</span>}
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// Bug Report Modal
// ═══════════════════════════════════════════════════════════════

const BUG_CATEGORIES = ['UI', 'Video', 'Access Control', 'Monitoring', 'Intercoms', 'Entry', 'Network', 'Registration', 'Shabbat Mode', 'Other'];

const BugReportModal = ({ onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Other');
  const [severity, setSeverity] = useState('minor');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setScreenshot(file);
    } else if (file) {
      toast.error('File must be under 10MB');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setScreenshot(file);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('category', category);
      formData.append('severity', severity);
      formData.append('pageUrl', window.location.pathname);
      if (screenshot) {
        formData.append('screenshot', screenshot);
      }
      await onSubmit(formData);
      onClose();
    } catch (err) {
      // handled by parent
    }
    setSubmitting(false);
  };

  return (
    <div className="nc-modal-overlay" onClick={onClose}>
      <div className="nc-modal" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bug size={18} /> Report a Bug
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Brief summary of the issue..."
              style={{
                width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px', fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px', fontFamily: 'inherit', outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                {BUG_CATEGORIES.map(cat => (
                  <option key={cat} value={cat} style={{ background: 'var(--bg-surface)' }}>{cat}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>Severity</label>
              <select
                value={severity}
                onChange={e => setSeverity(e.target.value)}
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px', fontFamily: 'inherit', outline: 'none',
                  boxSizing: 'border-box'
                }}
              >
                <option value="minor" style={{ background: 'var(--bg-surface)' }}>Minor - cosmetic or low impact</option>
                <option value="major" style={{ background: 'var(--bg-surface)' }}>Major - feature broken or degraded</option>
                <option value="critical" style={{ background: 'var(--bg-surface)' }}>Critical - blocking or security issue</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What happened? What were you doing? What did you expect to happen?"
              style={{ minHeight: 100 }}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 4 }}>Screenshot (optional)</label>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: '1px dashed ' + (dragOver ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.15)'),
                borderRadius: 8, padding: '16px 12px', textAlign: 'center',
                background: dragOver ? 'rgba(59,130,246,0.05)' : 'transparent',
                transition: 'all 0.15s', cursor: 'pointer',
              }}
              onClick={() => document.getElementById('bug-screenshot-input').click()}
            >
              <input
                id="bug-screenshot-input"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {screenshot ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#60a5fa', fontSize: 13 }}>
                  <Upload size={14} />
                  {screenshot.name} ({(screenshot.size / 1024).toFixed(0)} KB)
                  <button
                    onClick={e => { e.stopPropagation(); setScreenshot(null); }}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
                  >x</button>
                </div>
              ) : (
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>
                  <Upload size={16} style={{ marginBottom: 4, display: 'block', margin: '0 auto 4px' }} />
                  Drop screenshot here or click to browse
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="nc-modal-actions" style={{ marginTop: 16 }}>
          <button className="nc-modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="nc-modal-submit"
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !description.trim()}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Send size={14} />
            {submitting ? 'Submitting...' : 'Submit Bug Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main Page Component
// ═══════════════════════════════════════════════════════════════

const NotificationCenter = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(notificationsTutorial);

  // Data state
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ unresolved: 0, critical: 0, warnings: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state
  const [category, setCategory] = useState('ALL');
  const [severity, setSeverity] = useState(null);
  const [status, setStatus] = useState('ACTIVE');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Modal state
  const [modalNotification, setModalNotification] = useState(null);
  const [showBugModal, setShowBugModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [category, severity, status, searchDebounced, dateFrom, dateTo]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        category,
        page: String(page),
        pageSize: '25',
      });
      if (severity) params.set('severity', severity);
      if (status) params.set('status', status);
      if (searchDebounced) params.set('search', searchDebounced);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);

      const res = await fetchWithTenant(`/api/notifications?${params}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setNotifications(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error('[NotificationCenter] fetch error:', error);
      toast.error('Failed to load notifications');
    }
    setLoading(false);
  }, [category, severity, status, searchDebounced, dateFrom, dateTo, page]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/notifications/stats');
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('[NotificationCenter] stats error:', error);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ─── Actions ─────────────────────────────────────────────

  const handleResolve = async (notificationId, actionType, notes) => {
    try {
      const endpoint = actionType === 'bug' ? 'report-bug' : 'resolve';
      const res = await fetchWithTenant(`/api/notifications/${notificationId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Action failed');
      }
      toast.success(actionType === 'bug' ? 'Bug reported to Serverli' : 'Notification resolved');
      fetchNotifications();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Action failed');
      throw error;
    }
  };

  const handleAcknowledge = async (notificationId) => {
    try {
      const res = await fetchWithTenant(`/api/notifications/${notificationId}/acknowledge`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to acknowledge');
      toast.success('Notification acknowledged');
      fetchNotifications();
      fetchStats();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await fetchWithTenant('/api/notifications/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error('Bulk action failed');
      const result = await res.json();
      toast.success(`${result.success} notifications ${action}d`);
      setSelectedIds(new Set());
      fetchNotifications();
      fetchStats();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const handleSubmitBug = async (formData) => {
    try {
      const tenantId = localStorage.getItem('currentTenantId');
      const headers = {};
      if (tenantId) headers['x-tenant-id'] = tenantId;
      const res = await fetch('/api/notifications/submit-bug', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to submit');
      }
      const data = await res.json();
      toast.success('Bug reported: ' + data.bugCode + '. Our team has been notified.');
      fetchNotifications();
      fetchStats();
    } catch (error) {
      toast.error(error.message || 'Failed to submit bug report');
      throw error;
    }
  };

  // ─── Selection ───────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  };

  // ─── Group by date ──────────────────────────────────────

  const groupedNotifications = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    for (const n of notifications) {
      const label = getDateGroupLabel(n.createdAt);
      if (!currentGroup || currentGroup.label !== label) {
        currentGroup = { label, items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push(n);
    }

    return groups;
  }, [notifications]);

  // Category counts from stats
  const catCounts = {
    ALL: stats.unresolved + stats.today,
    MONITORING: (stats.bySource?.systemAlerts || 0) + (stats.bySource?.healthErrors || 0),
    AUDIT: stats.bySource?.auditToday || 0,
    INTEGRATION: stats.bySource?.integrations || 0,
  };

  return (
    <div className="page-container notification-center">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">
            <Bell size={28} />
            Notification Center
            {stats.unresolved > 0 && (
              <span style={{
                fontSize: 13, fontWeight: 500, padding: '3px 10px',
                borderRadius: 10, background: 'rgba(239,68,68,0.12)',
                color: '#ef4444', marginLeft: 10,
              }}>
                {stats.unresolved} unresolved
              </span>
            )}
          </h1>
          <p className="page-subtitle">
            System alerts, health errors, audit trail, and intercom events
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="tutorial-help-btn" onClick={() => startTutorial(notificationsTutorial.module, notificationsTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button
            className="btn btn-sm"
            style={{ background: 'rgba(249,115,22,0.1)', borderColor: 'rgba(249,115,22,0.3)', color: '#fb923c' }}
            onClick={() => setShowBugModal(true)}
          >
            <Bug size={14} /> Report Bug
          </button>
          <button
            className="btn btn-sm"
            onClick={() => { fetchNotifications(); fetchStats(); }}
            title="Refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Stats Cards */}
        <div className="nc-stats-grid">
          <div className="nc-stat-card">
            <div className="nc-stat-label">Unresolved</div>
            <div className="nc-stat-value">{stats.unresolved}</div>
          </div>
          <div className="nc-stat-card">
            <div className="nc-stat-label">Critical</div>
            <div className="nc-stat-value critical">{stats.critical}</div>
          </div>
          <div className="nc-stat-card">
            <div className="nc-stat-label">Warnings</div>
            <div className="nc-stat-value warning">{stats.warnings}</div>
          </div>
          <div className="nc-stat-card">
            <div className="nc-stat-label">Today</div>
            <div className="nc-stat-value">{stats.today}</div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="nc-tabs">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              className={`nc-tab ${category === cat.key ? 'active' : ''}`}
              onClick={() => setCategory(cat.key)}
            >
              {cat.label}
              <span className="nc-tab-count">{catCounts[cat.key] || 0}</span>
            </button>
          ))}
        </div>

        {/* Search + Filter Controls */}
        <div className="nc-controls">
          <div className="nc-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search by device, user, message, IP..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <X
                size={14}
                style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
                onClick={() => setSearch('')}
              />
            )}
          </div>

          <button
            className={`nc-filter-pill ${status === 'ACTIVE' ? 'active' : ''}`}
            onClick={() => setStatus(status === 'ACTIVE' ? null : 'ACTIVE')}
          >
            <AlertCircle size={13} /> Active
          </button>

          <button
            className={`nc-filter-pill ${severity === 'CRITICAL' ? 'active' : ''}`}
            onClick={() => setSeverity(severity === 'CRITICAL' ? null : 'CRITICAL')}
          >
            <XCircle size={13} /> Critical
          </button>

          <button
            className={`nc-filter-pill ${status === 'RESOLVED' ? 'active' : ''}`}
            onClick={() => setStatus(status === 'RESOLVED' ? null : 'RESOLVED')}
          >
            <CheckCircle size={13} /> Resolved
          </button>

          <button
            className={`nc-filter-pill ${showDateFilters ? 'active' : ''}`}
            onClick={() => setShowDateFilters(!showDateFilters)}
          >
            <Filter size={13} /> Date
          </button>
        </div>

        {/* Date Range Filters (collapsible) */}
        {showDateFilters && (
          <div className="nc-date-filters">
            <span className="nc-date-label">From:</span>
            <input
              type="date"
              className="nc-date-input"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
            <span className="nc-date-label">To:</span>
            <input
              type="date"
              className="nc-date-input"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
            {(dateFrom || dateTo) && (
              <button
                className="nc-filter-pill"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                style={{ marginLeft: 4 }}
              >
                <X size={12} /> Clear dates
              </button>
            )}
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="nc-bulk-bar">
            <input
              type="checkbox"
              className="nc-checkbox"
              checked={selectedIds.size === notifications.length}
              onChange={selectAll}
            />
            <span className="nc-bulk-count">{selectedIds.size} selected</span>
            <button className="nc-btn resolve" onClick={() => handleBulkAction('resolve')}>
              <CheckCircle size={12} /> Resolve All
            </button>
            <button className="nc-btn" onClick={() => handleBulkAction('acknowledge')}>
              <Eye size={12} /> Acknowledge All
            </button>
            <button className="nc-btn-clear" onClick={() => setSelectedIds(new Set())}>
              Clear selection
            </button>
          </div>
        )}

        {/* Notification List */}
        {loading ? (
          <div className="nc-loading">
            <div className="spinner" />
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="nc-empty">
            <BellOff size={40} />
            <h3>No notifications found</h3>
            <p>
              {search || severity || (status && status !== 'ACTIVE') || dateFrom
                ? 'Try adjusting your filters'
                : 'All systems operating normally'
              }
            </p>
          </div>
        ) : (
          <div className="nc-list">
            {groupedNotifications.map(group => (
              <div key={group.label}>
                <div className="nc-date-separator">{group.label}</div>
                {group.items.map(item => (
                  <NotificationItem
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.has(item.id)}
                    onSelect={toggleSelect}
                    onResolve={handleResolve}
                    onAcknowledge={handleAcknowledge}
                    onOpenModal={(n, action) => { setModalNotification(n); setModalAction(action); }}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="nc-pagination">
            <span className="nc-page-info">
              Showing {((page - 1) * 25) + 1}-{Math.min(page * 25, total)} of {total} notifications
            </span>
            <div className="nc-page-buttons">
              <button
                className="nc-page-btn"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <button
                className="nc-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bug Report Modal */}
      {showBugModal && (
        <BugReportModal
          onClose={() => setShowBugModal(false)}
          onSubmit={handleSubmitBug}
        />
      )}

      {/* Action Modal */}
      {modalNotification && modalAction && (
        <ActionModal
          notification={modalNotification}
          actionType={modalAction}
          onClose={() => { setModalNotification(null); setModalAction(null); }}
          onSubmit={handleResolve}
        />
      )}

      {showPrompt && (
        <TutorialPrompt
          module={notificationsTutorial.module}
          moduleName={notificationsTutorial.moduleName}
          steps={notificationsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default NotificationCenter;
